import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { workflowAPI } from '../../lib/api';
import { errMsg } from '../../lib/workflow';
import { SubmitLinkCard } from '../workflow/SubmitLinkCard';
import { ProgressRing } from '../workflow/ProgressRing';
import { WeekChart } from '../workflow/WeekChart';
import { QueueSection } from '../workflow/QueueSection';
import { QueueCard } from '../workflow/QueueCard';
import { RequestChangesDialog } from '../workflow/RequestChangesDialog';
import { MarkLiveDialog } from '../workflow/MarkLiveDialog';
import { HomeHeader } from './HomeHeader';

const btn = 'h-8 rounded-xl text-xs font-bold transition-transform active:scale-95';

const GoalCard = ({ summary }) => {
  const today = summary?.today || {};
  const target = summary?.target || 20;
  const left = Math.max(target - (today.submitted || 0), 0);
  return (
    <div className="wf-card p-6 h-full space-y-4" data-testid="nayan-goal-card">
      <div className="flex items-center gap-5">
        <ProgressRing value={today.submitted || 0} target={target} testId="nayan-progress-ring" />
        <div className="space-y-1.5">
          <h2 className="font-jakarta text-lg font-bold text-[#2D2623]">Today's links</h2>
          <p className="text-sm text-[#6E635B]" data-testid="nayan-goal-message">
            {left === 0 ? 'Target smashed! 🏆 Keep the streak going.' : `${left} more to hit ${target} today 💪`}
          </p>
          <div className="flex flex-wrap gap-2 text-[11px] font-semibold">
            <span className="rounded-full bg-[#ECFDF5] text-[#065F46] px-2.5 py-1" data-testid="nayan-live-today">🛍️ {today.marked_live || 0} live on Etsy</span>
            <span className="rounded-full bg-[#E0E7FF] text-[#3730A3] px-2.5 py-1" data-testid="nayan-approved-today">✅ {today.approved || 0} approved</span>
          </div>
        </div>
      </div>
      <WeekChart
        series={summary?.series}
        testId="nayan-week-chart"
        bars={[
          { key: 'submitted', label: 'Links', color: '#E07A5F' },
          { key: 'marked_live', label: 'Live on Etsy', color: '#81B29A' },
        ]}
      />
    </div>
  );
};

export const NayanHome = ({ data, user, embedded }) => {
  const { summary, items, reload } = data;
  const navigate = useNavigate();
  const [changesFor, setChangesFor] = useState(null);
  const [liveFor, setLiveFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const of = (...st) => items.filter((i) => st.includes(i.stage));
  const pushPanel = (id) => navigate(`/etsy/${id}?mp=etsy&focus=push`);

  const approve = async (item) => {
    setBusyId(item.id);
    try {
      await workflowAPI.approve(item.id);
      toast.success('Approved ✅ Opening the Etsy push panel…');
      pushPanel(item.id);
    } catch (e) {
      toast.error(errMsg(e, 'Could not approve'));
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-8" data-testid="nayan-home">
      {!embedded && (
        <HomeHeader emoji="👨‍💻" name={user?.name} subtitle="Submit today's links, review Nivedit's visuals and get them live on Etsy. Let's go! 🚀" />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7"><SubmitLinkCard onSubmitted={reload} /></div>
        <div className="lg:col-span-5"><GoalCard summary={summary} /></div>
      </div>

      <QueueSection
        emoji="👀" title="Waiting for your review" testId="nayan-review-queue"
        subtitle="Nivedit finished the visuals. Approve to push to Etsy, or send it back with changes."
        items={of('in_review')} empty="Nothing to review right now ☕"
        render={(it, i) => (
          <QueueCard key={it.id} item={it} index={i}>
            <Button onClick={() => approve(it)} disabled={busyId === it.id} className={`${btn} bg-[#81B29A] hover:bg-[#6FA38B] text-white`} data-testid={`nayan-approve-button-${it.id}`}>
              {busyId === it.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : null} Approve ✅
            </Button>
            <Button variant="outline" onClick={() => setChangesFor(it)} className={`${btn} border-[#FECDD3] text-[#9F1239] hover:bg-[#FFF1F2]`} data-testid={`nayan-request-changes-button-${it.id}`}>Request changes 🔁</Button>
            <Button variant="ghost" onClick={() => navigate(`/etsy/${it.id}`)} className={`${btn} text-[#6E635B]`} data-testid={`nayan-open-button-${it.id}`}>Open 👁️</Button>
          </QueueCard>
        )}
      />

      <QueueSection
        emoji="🚀" title="Approved: push & mark live" testId="nayan-approved-queue"
        subtitle="Push the draft from the Etsy panel (edit anything first), publish it on Etsy, then mark it live."
        items={of('approved')} empty="No approved listings waiting ✨"
        render={(it, i) => (
          <QueueCard key={it.id} item={it} index={i} showNotes={false}>
            <Button onClick={() => pushPanel(it.id)} className={`${btn} bg-[#F97316] hover:bg-[#EA580C] text-white`} data-testid={`nayan-push-etsy-button-${it.id}`}>
              {it.etsy_listing?.listing_id ? 'Update Etsy draft 🛒' : 'Push to Etsy 🛒'}
            </Button>
            <Button onClick={() => setLiveFor(it)} className={`${btn} bg-[#2D2623] hover:bg-black text-white`} data-testid={`nayan-mark-live-etsy-button-${it.id}`}>Mark live on Etsy 🛍️</Button>
          </QueueCard>
        )}
      />

      <QueueSection
        emoji="🔁" title="Changes sent back to Nivedit" testId="nayan-changes-queue"
        subtitle="These come back to your review queue once Nivedit resubmits."
        items={of('changes_requested')} empty="No open change requests 🙌"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i} showNotes={false} />}
      />

      <QueueSection
        emoji="🔗" title="With Nivedit now" testId="nayan-recent-queue"
        subtitle="Links you submitted that are still getting visuals."
        items={of('new', 'in_visuals').slice(0, 10)} empty="Submit a link above to get started 👆"
        render={(it, i) => <QueueCard key={it.id} item={it} index={i} />}
      />

      <RequestChangesDialog item={changesFor} onClose={() => setChangesFor(null)} onDone={reload} />
      <MarkLiveDialog item={liveFor} onClose={() => setLiveFor(null)} onDone={reload} />
    </div>
  );
};
